import {useMutation, useQueryClient} from 'react-query';

import {type IStandardErrorResponse} from 'src/api/common';
import {
    type EMessageChannel,
    EMessageDirection,
    EMessageSentStatus,
    type IMessage,
    type ISendMessageResponse
} from 'src/services/api/messages/types';
import {useNotifications} from 'src/notifications';
import {apiClient} from 'src/services/api/client';
import {queryKeys} from 'src/services/api/constants';
import {getApiErrorMessage} from 'src/services/api/utils';
import {
    type IGetLeadMessagesWithAttachmentsResponse,
    useLoggedInUser
} from 'src/services/api';

export const SEND_MESSAGE_PATH = 'api/landlord/messages/send';

export type ISendMessageRequest = {
    leadUuid: string;
    channel: EMessageChannel;
    message: string;
    attachments?: File[];
    is_bulk_send?: boolean;
    snippet_id?: number;
    /** BCP-47 language code to translate the message to before sending (e.g. "es"). */
    translate_to_lang?: string;
}

export const useSendMessage = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { data: loggedInUser } = useLoggedInUser();

    return useMutation<
        ISendMessageResponse['new_message'],
        IStandardErrorResponse,
        ISendMessageRequest
    >(
        async (payload) => {
            const formData = new FormData();
            formData.append('lead_uuid', payload.leadUuid);
            formData.append('channel', payload.channel);
            formData.append('contents', payload.message);
            if (payload.attachments) {
                payload.attachments.forEach((attachment, index) => {
                    formData.append(`attachments${index}`, attachment, attachment.name);
                });
            }
            if (payload.is_bulk_send) {
                formData.append('is_bulk_send', 'true');
            }
            if (payload.snippet_id != null) {
                formData.append('snippet_id', payload.snippet_id.toString());
            }
            if (payload.translate_to_lang) {
                formData.append('translate_to_lang', payload.translate_to_lang);
            }

            const tempMessage: IMessage = {
                uuid: 'placeholder-' + Math.random(),
                owner: loggedInUser!.id,
                channel: payload.channel,
                contents: payload.message,
                direction: EMessageDirection.OUT,
                sent_status: EMessageSentStatus.SENT,
                is_sent_by_automation: false,
                is_rental_application_request: false,
                inserted_at: new Date(),
            };

            queryClient.setQueryData(
                [queryKeys.messages, payload.leadUuid],
                (oldData: IGetLeadMessagesWithAttachmentsResponse | IMessage[] | undefined): IGetLeadMessagesWithAttachmentsResponse | IMessage[] => {
                    if (!oldData) {
                        return [tempMessage];
                    }
                    if (Array.isArray(oldData)) {
                        return [...oldData, tempMessage];
                    }
                    return {
                        ...oldData,
                        messages: [...oldData.messages, tempMessage],
                    };
                }
            );

            const res = await apiClient(SEND_MESSAGE_PATH, {
                method: 'post',
                body: formData,
                timeout: 30000,
            }).json<ISendMessageResponse>();

            return res.new_message;
        }, {
            onSuccess: (_data, variables) => {
                addNotification('Message sent', 'success');
                queryClient.invalidateQueries([queryKeys.conversations, 'lead', variables.leadUuid]);
                queryClient.invalidateQueries(queryKeys.achievements);
                // Delay the messages invalidation so the server has time to index
                // the new message.
                setTimeout(() => {
                    queryClient.invalidateQueries([queryKeys.messages, variables.leadUuid]);
                }, 500);
            },
            onError: (error, payload) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to send message';
                addNotification(errorMessage, 'error');
                queryClient.invalidateQueries([queryKeys.messages, payload.leadUuid]);
            }
        }
    );
};
